// Plan Library v2 — charge resolution core (docs/PLAN_RATING_ACCEPTANCE_SPEC.md).
//
// Pure functions only: the DB service (server/services/chargeService.ts) is a
// thin I/O wrapper so every acceptance scenario is unit-testable.
//
// The model, in one paragraph: an employer's plan link locks BOTH its rate
// version and its group rating area at the link's own plan-year start, for 12
// months (scenarios 04/10/15/17). The service month never selects a version —
// it selects a plan year, whose start selects the version. Rating is
// group-level on employer-location plans: member work locations never touch
// rates (01/13/14/16). Posted charges are immutable; every change materializes
// as reversal + (optionally) re-charge rows computed by computeLedgerDeltas.

import {
  AgeDeterminationRule,
  calculateAge,
  createCoverageMonth,
  getRatingDate,
  parseAgeBandLabel,
} from "../lib/ageDetermination";
import { narrowRatesToRatingArea, resolvePlanRates, type VersionedRateRow } from "./planRateResolution";

// ---------------------------------------------------------------------------
// Plan-year window
// ---------------------------------------------------------------------------

export function toDate(d: Date | string): Date {
  if (d instanceof Date) {
    // Normalize to local midnight of the UTC calendar date, matching the
    // date-only string branch below - comparing an as-is UTC instant
    // against a locally-parsed one skews boundary comparisons by the
    // server's UTC offset (can flip which version a boundary date resolves to).
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  // Date-only strings parse as UTC; pin to local midnight for stable math.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(d);
}

export function firstOfMonth(d: Date | string): Date {
  const t = toDate(d);
  return new Date(t.getFullYear(), t.getMonth(), 1);
}

export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * The start of the plan year containing `serviceMonth` for a link whose plan
 * years are 12-month windows anchored at `linkEffectiveDate` (scenario 10:
 * Gamma's 2027-05 service month still belongs to the plan year that began
 * 2026-06-01).
 */
export function planYearStartFor(linkEffectiveDate: Date | string, serviceMonth: Date | string): Date {
  const anchor = toDate(linkEffectiveDate);
  const month = firstOfMonth(serviceMonth);
  let start = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
  if (start > month) return start; // service month precedes link start — clamp to first plan year
  for (;;) {
    const next = new Date(start.getFullYear() + 1, start.getMonth(), start.getDate());
    if (next > month) return start;
    start = next;
  }
}

// ---------------------------------------------------------------------------
// Version resolution (plan-year lock)
// ---------------------------------------------------------------------------

export interface RateVersionLike {
  id?: number;
  effectiveStart: string | Date;
  effectiveEnd?: string | Date | null;
  revision?: number | null;
  clientId?: number | null;
  label?: string | null;
}

/**
 * The version applying to plan years BEGINNING at `planYearStart`: window
 * [effectiveStart, effectiveEnd) covers the plan-year start; the highest
 * revision wins (a correction supersedes the version it corrects — scenario
 * 21). Returns null when no version covers (legacy versionless data — caller
 * falls back to resolvePlanRates on effectiveStart windows).
 */
export function resolveVersionForPlanYear(
  versions: RateVersionLike[] | null | undefined,
  planYearStart: Date | string,
  clientId?: number | null,
): RateVersionLike | null {
  const pys = toDate(planYearStart).getTime();
  let pool = (versions || []).filter((v) => {
    const start = toDate(v.effectiveStart).getTime();
    const end = v.effectiveEnd == null ? Infinity : toDate(v.effectiveEnd).getTime();
    return start <= pys && pys < end;
  });
  if (clientId != null) {
    const clientScoped = pool.filter((v) => v.clientId != null && Number(v.clientId) === Number(clientId));
    pool = clientScoped.length > 0 ? clientScoped : pool.filter((v) => v.clientId == null);
  } else {
    pool = pool.filter((v) => v.clientId == null);
  }
  if (pool.length === 0) return null;
  return pool.reduce((best, v) =>
    (v.revision ?? 1) > (best.revision ?? 1) ||
    ((v.revision ?? 1) === (best.revision ?? 1) && (v.id ?? 0) > (best.id ?? 0))
      ? v
      : best,
  );
}

export function versionKeyOf(version: RateVersionLike | null): string {
  if (!version) return ""; // legacy null-start version
  const start = isoDate(toDate(version.effectiveStart));
  const rev = version.revision ?? 1;
  return rev > 1 ? `${start} r${rev}` : start;
}

// ---------------------------------------------------------------------------
// Tobacco / RAS (scenarios 03/18)
// ---------------------------------------------------------------------------

export interface TobaccoFields {
  tobaccoUser?: string | null; // "Yes" | "No" | "Cessation Program" (legacy) | null
  rasStatus?: string | null; // "none" | "enrolled" | "completed" | null
}

export function isAttestedTobaccoUser(emp: TobaccoFields): boolean {
  const t = (emp.tobaccoUser || "").trim().toLowerCase();
  return t === "yes" || t === "cessation program";
}

/** RAS satisfied while enrolled in or having completed a qualifying program. */
export function isRasSatisfied(emp: TobaccoFields): boolean {
  const ras = (emp.rasStatus || "none").trim().toLowerCase();
  if (ras === "enrolled" || ras === "completed") return true;
  // Legacy single-field data: "Cessation Program" meant attested user already
  // enrolled in an alternative program.
  return (emp.tobaccoUser || "").trim().toLowerCase() === "cessation program";
}

/** Surcharge applies while attested user AND the RAS is unsatisfied. */
export function tobaccoSurchargeApplies(emp: TobaccoFields): boolean {
  return isAttestedTobaccoUser(emp) && !isRasSatisfied(emp);
}

// ---------------------------------------------------------------------------
// Age determination + mid-year re-determination trigger (scenarios 02/11/19)
// ---------------------------------------------------------------------------

export type MidYearAgeTrigger = "none" | "birthday_month" | "policy_anniversary";

export interface AgeResolutionInput {
  dob: Date | string;
  /** Plan's Age Determination setting (AgeDeterminationRule value). */
  ageDetermination?: string | null;
  /** Plan's mid-year re-determination trigger; default "none" (ACA). */
  midYearTrigger?: string | null;
  planYearStart: Date | string;
  serviceMonth: Date | string;
  /** Member's own coverage start (mid-year joiners under plan_anniversary). */
  memberCoverageStart?: Date | string | null;
}

/**
 * Age + determination date for one member-month.
 * - trigger "none" / "policy_anniversary": the configured Age Determination
 *   rule is evaluated once per plan year (at the plan-year's first month), so
 *   age is locked for the year (scenario 02; band edge at the determination
 *   date per scenario 11).
 * - trigger "birthday_month": age steps up the first month FOLLOWING the
 *   birthday (attained age at the first of the service month — scenario 19).
 */
export function resolveChargeAge(input: AgeResolutionInput): { age: number; determinationDate: Date } {
  const trigger = ((input.midYearTrigger || "none").trim().toLowerCase().replace(/-/g, "_")) as MidYearAgeTrigger;
  const pys = toDate(input.planYearStart);
  const serviceStart = firstOfMonth(input.serviceMonth);

  if (trigger === "birthday_month") {
    return { age: calculateAge(toDate(input.dob), serviceStart), determinationDate: serviceStart };
  }

  const rule = input.ageDetermination || AgeDeterminationRule.PLAN_ANNIVERSARY;
  // Lock for the plan year: evaluate the rule at the plan-year's first month.
  const coverageMonth = createCoverageMonth(pys.getFullYear(), pys.getMonth() + 1);
  const determinationDate = getRatingDate(
    rule,
    {
      dob: toDate(input.dob),
      personCoverageStartDate: input.memberCoverageStart ? toDate(input.memberCoverageStart) : undefined,
    },
    coverageMonth,
    { planEffectiveDate: pys, ageDetermination: rule },
  );
  return { age: calculateAge(toDate(input.dob), determinationDate), determinationDate };
}

/** Find the age-band row containing `age` (e.g. "35-39", "64+", "35"). */
export function findAgeBandRow<T extends { ageBand?: string | null }>(rows: T[], age: number): T | undefined {
  let best: T | undefined;
  let bestMin = -1;
  for (const row of rows) {
    if (!row.ageBand) continue;
    const { minAge, maxAge } = parseAgeBandLabel(row.ageBand);
    if (Number.isNaN(minAge)) continue;
    if (age >= minAge && (maxAge == null || age <= maxAge) && minAge > bestMin) {
      best = row;
      bestMin = minAge;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Versioned rate-row selection (shared by charges, premiums and quoting)
// ---------------------------------------------------------------------------

/**
 * Select the rate rows in force for a plan-year start, preferring
 * version-metadata-linked rows (plan_rate_versions — handles corrections,
 * where two revisions share one effectiveStart) and falling back to legacy
 * effectiveStart-window resolution. Never resolves at a service month.
 */
export function resolveVersionedPlanRates(
  rateRows: VersionedRateRow[],
  versions: RateVersionLike[] | null | undefined,
  opts: { clientId?: number | null; ratingAreaId?: number | null; planYearStart: Date | string },
): { rows: VersionedRateRow[]; version: RateVersionLike | null } {
  const version = resolveVersionForPlanYear(versions, opts.planYearStart, opts.clientId);
  if (version?.id != null) {
    const rows = narrowRatesToRatingArea(
      rateRows.filter((r) => r.versionId === version.id && (r.isActive == null || r.isActive === true)),
      opts.ratingAreaId,
    );
    if (rows.length > 0) return { rows, version };
  }
  // No version metadata, or metadata exists but rows aren't version-linked
  // (legacy writes): resolve by effectiveStart windows at the plan-year start.
  return {
    rows: resolvePlanRates(rateRows, {
      clientId: opts.clientId,
      ratingAreaId: opts.ratingAreaId ?? null,
      asOf: opts.planYearStart,
    }),
    version,
  };
}

// ---------------------------------------------------------------------------
// Member-month charge resolution
// ---------------------------------------------------------------------------

export interface ChargeResolutionInput {
  plan: {
    id: number;
    planCode?: string | null;
    rateStructureType?: string | null; // age_rates | tier_rates | ...
    ageDetermination?: string | null;
    midYearAgeRedetermination?: string | null;
    ratingBasis?: string | null;
  };
  clientId: number;
  /** Plan-year anchor: the client_plans link effective date. */
  linkEffectiveDate: Date | string;
  /** Group rating area derived from the employer's primary location at plan-year start. */
  groupRatingAreaId?: number | null;
  /** Version metadata for the plan (plan_rate_versions). */
  versions?: RateVersionLike[] | null;
  /** All candidate rate rows for the plan (any client/area/version). */
  rateRows: VersionedRateRow[];
  member: {
    enrollmentId: number;
    employeeId: number;
    dependentId?: number | null;
    dob?: Date | string | null;
    tobaccoUser?: string | null;
    rasStatus?: string | null;
    /** When the current tobacco attestation was made. The surcharge applies
     * only to service months STARTING AFTER this date (scenario 03 —
     * prospective, never retroactive), so regenerating earlier months
     * reproduces their original non-tobacco resolution. Null = no dated
     * attestation; the current state applies to all months. */
    tobaccoAttestedAt?: Date | string | null;
    coverageTier?: string | null;
    coverageStart?: Date | string | null;
    /** table_rates only: covered dependent counts this service month, used
     * to pick the coverage-combination row (see resolveCoverageTableLabel).
     * The caller computes these from sibling dependent enrollments - this
     * module has no enrollment/dependent tables to query itself. */
    spouseCount?: number;
    childCount?: number;
  };
  serviceMonth: Date | string;
}

/**
 * table_rates coverage-combination label from a family's composition -
 * mirrors the ladder in shared/utils/enrollmentCalculator.ts's quoting path
 * (kept in sync manually; that one runs off live-current dependents for
 * display, this one off historically-accurate per-month counts for billing).
 */
export function resolveCoverageTableLabel(spouseCount: number, childCount: number): string {
  if (spouseCount > 0 && childCount >= 3) return "Employee + 3+ Children + Spouse";
  if (spouseCount > 0 && childCount === 2) return "Employee + 2 Children + Spouse";
  if (spouseCount > 0 && childCount === 1) return "Employee + 1 Children + Spouse";
  if (spouseCount > 0) return "Employee + Spouse";
  if (childCount >= 3) return "Employee + 3+ Children";
  if (childCount === 2) return "Employee + 2 Children";
  if (childCount === 1) return "Employee + 1 Children";
  return "Employee";
}

export interface ResolvedMemberMonthCharge {
  enrollmentId: number;
  employeeId: number;
  dependentId: number | null;
  planId: number;
  serviceMonth: string; // ISO first-of-month
  amount: number;
  planYearStart: string;
  ratingAreaId: number | null;
  rateVersionId: number | null;
  rateVersionKey: string;
  rateStructureType: string | null;
  ageBand: string | null;
  tobaccoApplied: boolean | null;
  coverageTier: string | null;
  ageDeterminationDate: string | null;
  snapshot: Record<string, unknown>;
}

const normalizeTier = (t: string | null | undefined) => (t || "").trim().toLowerCase().replace(/[\s+&-]+/g, "_");

/**
 * Resolve one member-month to its charge key + amount, reading rates through
 * the plan-year lock. Returns null when no rate cell matches (caller decides
 * whether that's an error or a skip).
 */
export function resolveMemberMonthCharge(input: ChargeResolutionInput): ResolvedMemberMonthCharge | null {
  const serviceStart = firstOfMonth(input.serviceMonth);
  const pys = planYearStartFor(input.linkEffectiveDate, serviceStart);
  const { rows, version } = resolveVersionedPlanRates(input.rateRows, input.versions, {
    clientId: input.clientId,
    ratingAreaId: input.groupRatingAreaId ?? null,
    planYearStart: pys,
  });
  if (rows.length === 0) return null;

  const structure = (input.plan.rateStructureType || rows[0]?.rateStructureType || "").toString();
  const isComposite = structure === "tier_rates" || structure === "composite";

  let row: VersionedRateRow | undefined;
  let ageBand: string | null = null;
  let tobaccoApplied: boolean | null = null;
  let determinationDate: string | null = null;

  if (isComposite) {
    // Composite: tier only — demographic events have no rate effect (scenario 22).
    // The tier rate covers the whole family and is carried on the EMPLOYEE
    // enrollment row; dependent rows carry no separate premium.
    if (input.member.dependentId != null) return null;
    const tier = normalizeTier(input.member.coverageTier);
    row = rows.find((r) => normalizeTier(r.coverageTier as string) === tier);
  } else if (structure === "table_rates") {
    // Same shape as composite: one row per coverage combination, covering
    // the whole family, carried on the employee row only.
    if (input.member.dependentId != null) return null;
    const label = resolveCoverageTableLabel(input.member.spouseCount ?? 0, input.member.childCount ?? 0);
    row = rows.find((r) => String((r as any).coverageTable || "") === label);
  } else {
    if (input.member.dob == null) return null;
    const resolved = resolveChargeAge({
      dob: input.member.dob,
      ageDetermination: input.plan.ageDetermination,
      midYearTrigger: input.plan.midYearAgeRedetermination,
      planYearStart: pys,
      serviceMonth: serviceStart,
      memberCoverageStart: input.member.coverageStart ?? null,
    });
    determinationDate = isoDate(resolved.determinationDate);
    // age_tier_rates: multiple rows can share one age band (one per
    // coverage tier) - narrow to the member's tier BEFORE the age-band
    // search, or a tie on minAge silently picks an arbitrary tier's rate.
    const isAgeTier = structure === "age_tier_rates";
    const candidateRows = isAgeTier
      ? rows.filter((r) => normalizeTier(r.coverageTier as string) === normalizeTier(input.member.coverageTier))
      : rows;
    row = findAgeBandRow(candidateRows as Array<VersionedRateRow & { ageBand?: string | null }>, resolved.age);
    ageBand = (row?.ageBand as string) ?? null;
    tobaccoApplied = tobaccoSurchargeApplies(input.member);
    // Prospective-only (scenario 03): a dated attestation reaches service
    // months starting after it — attested 2026-06-20 → surcharge from 2026-07.
    if (tobaccoApplied && input.member.tobaccoAttestedAt != null) {
      const attestedAt = toDate(input.member.tobaccoAttestedAt);
      if (serviceStart <= attestedAt) tobaccoApplied = false;
    }
  }
  if (!row) return null;

  const base = Number(row.monthlyRate);
  const tobaccoRate = row.tobaccoMonthlyRate != null ? Number(row.tobaccoMonthlyRate) : null;
  const amount = tobaccoApplied && tobaccoRate != null ? tobaccoRate : base;
  if (!Number.isFinite(amount)) return null;

  return {
    enrollmentId: input.member.enrollmentId,
    employeeId: input.member.employeeId,
    dependentId: input.member.dependentId ?? null,
    planId: input.plan.id,
    serviceMonth: isoDate(serviceStart),
    amount: Math.round(amount * 100) / 100,
    planYearStart: isoDate(pys),
    ratingAreaId: input.groupRatingAreaId ?? null,
    rateVersionId: version?.id ?? null,
    rateVersionKey: versionKeyOf(version),
    rateStructureType: structure || null,
    ageBand,
    tobaccoApplied,
    coverageTier: input.member.coverageTier ?? null,
    ageDeterminationDate: determinationDate,
    snapshot: {
      planCode: input.plan.planCode ?? null,
      ratingBasis: input.plan.ratingBasis ?? "employer-location",
      ageDeterminationRule: input.plan.ageDetermination ?? null,
      midYearAgeRedetermination: input.plan.midYearAgeRedetermination ?? "none",
      versionLabel: version?.label ?? null,
    },
  };
}

// ---------------------------------------------------------------------------
// Ledger deltas — the ONLY way ledger state changes (scenarios 04/05/16/20/21)
// ---------------------------------------------------------------------------

export interface LedgerEntryLike {
  id: number;
  enrollmentId: number;
  serviceMonth: string | Date;
  entryType: string; // 'charge' | 'reversal'
  reversesChargeId?: number | null;
  amount: string | number;
  rateVersionKey?: string | null;
  ageBand?: string | null;
  tobaccoApplied?: boolean | null;
  coverageTier?: string | null;
  ratingAreaId?: number | null;
  planId?: number | null;
}

export interface LedgerDeltas {
  /** New charge rows to insert (from the `expected` shape). */
  charges: ResolvedMemberMonthCharge[];
  /** Reversals to insert: equal-and-opposite rows referencing the original. */
  reversals: Array<{ original: LedgerEntryLike; amount: number }>;
}

const monthKey = (m: string | Date) => isoDate(firstOfMonth(m));
const cents = (a: string | number) => Math.round(Number(a) * 100);

/** Charges not yet offset by a reversal, per (enrollmentId, serviceMonth). */
export function netActiveCharges(existing: LedgerEntryLike[]): Map<string, LedgerEntryLike[]> {
  const reversed = new Set<number>();
  for (const e of existing) {
    if (e.entryType === "reversal" && e.reversesChargeId != null) reversed.add(e.reversesChargeId);
  }
  const map = new Map<string, LedgerEntryLike[]>();
  for (const e of existing) {
    if (e.entryType !== "charge" || reversed.has(e.id)) continue;
    const key = `${e.enrollmentId}|${monthKey(e.serviceMonth)}`;
    const list = map.get(key) || [];
    list.push(e);
    map.set(key, list);
  }
  return map;
}

/**
 * Diff the ledger's net-active state against the expected member-months.
 * Immutability-preserving by construction:
 * - expected month with no net-active charge → new charge (04: retro enrollment)
 * - net-active charge with no expected month → reversal only (05: retro term)
 * - both present but amount OR resolution key differs → reversal + re-charge
 *   (20: backdated plan change; 21: rate correction; 22: tier change)
 * - both present and identical → NOTHING (01/16: no-op events; 21: unaffected
 *   cells stay silent)
 *
 * `existing` must cover every (enrollment, month) in scope; `expected` is the
 * desired state for exactly those enrollments/months being regenerated.
 */
export function computeLedgerDeltas(
  existing: LedgerEntryLike[],
  expected: ResolvedMemberMonthCharge[],
): LedgerDeltas {
  const active = netActiveCharges(existing);
  const charges: ResolvedMemberMonthCharge[] = [];
  const reversals: Array<{ original: LedgerEntryLike; amount: number }> = [];
  const expectedKeys = new Set<string>();

  for (const exp of expected) {
    const key = `${exp.enrollmentId}|${exp.serviceMonth}`;
    expectedKeys.add(key);
    const current = active.get(key) || [];
    // "Unchanged" = same charged substance (amount, plan, band/tier/tobacco).
    // The version key is deliberately NOT compared: a correction version that
    // leaves a cell's value untouched produces no deltas for that cell
    // (scenario 21 — unaffected cells stay silent), so the original charge
    // keeps its original version snapshot.
    const match = current.find(
      (c) =>
        cents(c.amount) === Math.round(exp.amount * 100) &&
        (c.ageBand ?? null) === exp.ageBand &&
        (c.tobaccoApplied ?? null) === exp.tobaccoApplied &&
        (c.coverageTier == null || exp.coverageTier == null || c.coverageTier === exp.coverageTier) &&
        (c.planId == null || c.planId === exp.planId),
    );
    if (match) {
      // Identical resolution already posted — stay silent. Reverse any extra
      // duplicates beyond the matching one.
      for (const dup of current) if (dup !== match) reversals.push({ original: dup, amount: -Number(dup.amount) });
      continue;
    }
    for (const stale of current) reversals.push({ original: stale, amount: -Number(stale.amount) });
    charges.push(exp);
  }

  // Net-active months no longer expected → reverse (retro termination).
  active.forEach((list, key) => {
    if (expectedKeys.has(key)) return;
    for (const c of list) reversals.push({ original: c, amount: -Number(c.amount) });
  });

  return { charges, reversals };
}

// ---------------------------------------------------------------------------
// Billability
// ---------------------------------------------------------------------------

export const CHARGEABLE_STATUSES = ["active", "cobra", "terminated"] as const;

/**
 * Whether an enrollment row represents billable coverage at all.
 *  - status must be one the ledger bills (active / cobra / terminated-with-window)
 *  - a waiver with no end date never provided coverage (broker/OE "waived"
 *    election rows are stored as status active + waivedCoverage) → never billed
 *  - a waiver WITH an end date is real coverage ending on a QLE waiver → billed
 *    up to the end date like any termination
 */
export function isChargeableEnrollment(enr: {
  status: string | null | undefined;
  waivedCoverage: boolean | null | undefined;
  endDate: Date | string | null | undefined;
}): boolean {
  if (!(CHARGEABLE_STATUSES as readonly string[]).includes(String(enr.status ?? ""))) return false;
  if (enr.waivedCoverage && !enr.endDate) return false;
  return true;
}
